import { useAuthState } from '@auth/hooks';
import { FEATURE_FLAGS, type FeatureFlag } from '@core/domain';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Feature flag state interface
 */
type FeatureFlagState = Record<FeatureFlag, boolean>;

/**
 * Feature flag context interface
 */
export interface FeatureFlagContextValue {
  featureFlags: FeatureFlagState;
  isFeatureEnabled: (flag: FeatureFlag) => boolean;
  toggleFeatureFlag: (flag: FeatureFlag, enabled?: boolean) => void;
  resetFeatureFlags: () => void;
}

/**
 * Feature flag context
 */
const FeatureFlagContext = createContext<FeatureFlagContextValue | undefined>(undefined);

/**
 * Hook to use feature flag context
 */
export function useFeatureFlagContext(): FeatureFlagContextValue {
  const context = useContext(FeatureFlagContext);

  if (!context) {
    throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
  }

  return context;
}

/**
 * Local storage key for feature flags
 */
const FEATURE_FLAGS_STORAGE_KEY = 'soteria_feature_flags';

/**
 * Get feature flag value from URL parameters
 */
function getURLFeatureFlagValue(featureFlag: FeatureFlag): boolean | undefined {
  if (typeof window === 'undefined') return undefined;

  const urlParams = new URLSearchParams(window.location.search);

  // Normalize parameter name to lowercase for case-insensitive comparison
  const paramName = `feature_${featureFlag.toLowerCase()}`;

  for (const [key, value] of urlParams.entries()) {
    if (key.toLowerCase() === paramName) {
      // Only accept exact 'true' or 'false' values (case insensitive)
      const normalizedValue = value.toLowerCase();

      if (normalizedValue === 'true') return true;
      if (normalizedValue === 'false') return false;

      return undefined;
    }
  }

  return undefined;
}

/**
 * Get feature flag value from localStorage
 */
function getStorageFeatureFlagValue(featureFlag: FeatureFlag): boolean | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    const storedFeatureFlags = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);

    if (!storedFeatureFlags) return undefined;

    const featureFlags = JSON.parse(storedFeatureFlags) as Record<string, boolean>;

    return featureFlags[featureFlag];
  } catch {
    return undefined;
  }
}

/**
 * Get feature flag value from environment variables
 */
function getEnvFeatureFlagValue(featureFlag: FeatureFlag): boolean | undefined {
  const featureFlagKey = `VITE_FEATURE_${featureFlag}`;
  const envValue = import.meta.env[featureFlagKey];

  if (envValue === undefined) return undefined;

  const stringValue = String(envValue);

  if (stringValue === 'true') return true;
  if (stringValue === 'false') return false;

  return undefined;
}

/**
 * Resolve feature flag value using priority system
 * Priority (for admins): URL > localStorage > environment > default
 * Priority (for non-admins): environment > default (ignores URL and localStorage)
 */
function resolveFeatureFlagValue(featureFlag: FeatureFlag, isSystemAdmin: boolean): boolean {
  // For non-admin users, only respect environment variables and defaults
  if (!isSystemAdmin) {
    // Check environment variables
    const envValue = getEnvFeatureFlagValue(featureFlag);

    if (envValue !== undefined) return envValue;

    // Default to false if not set in environment
    return false;
  }

  // For admin users, use full priority system
  // 1. Check URL parameters (highest priority)
  const urlValue = getURLFeatureFlagValue(featureFlag);

  if (urlValue !== undefined) return urlValue;

  // 2. Check localStorage
  const storageValue = getStorageFeatureFlagValue(featureFlag);

  if (storageValue !== undefined) return storageValue;

  // 3. Check environment variables
  const envValue = getEnvFeatureFlagValue(featureFlag);

  if (envValue !== undefined) return envValue;

  // 4. Default to false if not set anywhere
  return false;
}

/**
 * Save feature flags to localStorage
 */
function saveFeatureFlagsToStorage(features: FeatureFlagState): void {
  if (typeof window === 'undefined') return;

  try {
    // Only save features that are different from their resolved defaults
    // (which includes environment variables and code defaults)
    const featuresFlagsToSave: Record<string, boolean> = {};

    Object.entries(features).forEach(([featureFlag, isEnabled]) => {
      // Get the resolved default value (environment > code default)
      const envValue = getEnvFeatureFlagValue(featureFlag as FeatureFlag);
      const resolvedDefault = envValue !== undefined ? envValue : false;

      // Only save if different from the resolved default
      if (isEnabled !== resolvedDefault) {
        featuresFlagsToSave[featureFlag] = isEnabled;
      }
    });

    if (Object.keys(featuresFlagsToSave).length > 0) {
      localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(featuresFlagsToSave));
    } else {
      localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Failed to save feature toggles to localStorage:', error);
  }
}

/**
 * Initialize feature flags state
 */
function initializeFeatureFlags(isSystemAdmin: boolean): FeatureFlagState {
  const featureFlags: FeatureFlagState = {} as FeatureFlagState;

  // Initialize all feature flags
  Object.values(FEATURE_FLAGS).forEach(featureFlag => {
    featureFlags[featureFlag] = resolveFeatureFlagValue(featureFlag, isSystemAdmin);
  });

  return featureFlags;
}

/**
 * Feature flag provider props
 */
export interface FeatureFlagProviderProps {
  children: ReactNode;
}

/**
 * Feature flag provider component
 */
export function FeatureFlagProvider({ children }: FeatureFlagProviderProps): React.ReactElement {
  const { isUserSystemAdmin } = useAuthState();
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagState>(() => initializeFeatureFlags(isUserSystemAdmin));

  // Reinitialize feature flags when isSystemAdmin changes or URL changes
  useEffect(() => {
    const initializeFlags = () => {
      setFeatureFlags(initializeFeatureFlags(isUserSystemAdmin));
    };

    // Initialize flags on isUserSystemAdmin change
    initializeFlags();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', initializeFlags);

    return () => {
      window.removeEventListener('popstate', initializeFlags);
    };
  }, [isUserSystemAdmin]);

  /**
   * Check if a feature is enabled
   */
  const isFeatureEnabled = (featureFlag: FeatureFlag): boolean => featureFlags[featureFlag] ?? false;

  /**
   * Toggle a feature flag on/off (only allowed for admins)
   */
  const toggleFeatureFlag = (featureFlag: FeatureFlag, isEnabled?: boolean): void => {
    if (!isUserSystemAdmin) {
      return;
    }

    setFeatureFlags(prevFeatureFlags => {
      const newIsEnabled = isEnabled !== undefined ? isEnabled : !prevFeatureFlags[featureFlag];
      const newFeatureFlags = { ...prevFeatureFlags, [featureFlag]: newIsEnabled };

      saveFeatureFlagsToStorage(newFeatureFlags);

      return newFeatureFlags;
    });
  };

  /**
   * Reset all feature flags to their default values (only allowed for admins)
   */
  const resetFeatureFlags = (): void => {
    if (!isUserSystemAdmin) {
      return;
    }

    if (typeof window !== 'undefined') {
      localStorage.removeItem(FEATURE_FLAGS_STORAGE_KEY);
    }

    setFeatureFlags(initializeFeatureFlags(isUserSystemAdmin));
  };

  const contextValue: FeatureFlagContextValue = {
    featureFlags,
    isFeatureEnabled,
    toggleFeatureFlag,
    resetFeatureFlags,
  };

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
}
